import React from 'react';
import type { SectorLeaderboardRow } from '../utils/raceSector';
import { formatSectorTime } from '../utils/raceSector';

interface RaceSectorResultsProps {
    hasSector: boolean;
    rows: SectorLeaderboardRow[];
    incomplete: { athleteId: string; name: string }[];
    sectorDistanceMiles?: number | null;
    onRowClick?: (athleteId: string) => void;
    spotlightedAthleteId?: string | null;
    currentUserId?: string;
}

const RaceSectorResults: React.FC<RaceSectorResultsProps> = ({
    hasSector, rows, incomplete, sectorDistanceMiles, onRowClick, spotlightedAthleteId, currentUserId
}) => {
    if (!hasSector) {
        return (
            <div className="flex-grow flex flex-col items-center justify-center text-gray-500 p-6 text-center border border-dashed border-gray-700 rounded-lg">
                <span className="text-2xl mb-2">⚡</span>
                <p className="mb-1 font-medium text-gray-400">No Race Sector set for this ride.</p>
                <p className="text-xs">The ride host can mark a Race Sector on the route to rank riders here.</p>
            </div>
        );
    }

    // Hide the power column when no rider's file has power data
    const showPower = rows.some(r => r.effort.avgPower !== null);

    return (
        <div className="flex flex-col flex-grow min-h-0">
            <div className="flex items-center justify-between mb-2 text-xs">
                <span className="font-bold text-cyan-300 uppercase tracking-wide flex items-center gap-1.5">
                    <span>⚡</span> Race Sector
                </span>
                {sectorDistanceMiles ? <span className="text-gray-400">{sectorDistanceMiles} mi</span> : null}
            </div>

            {rows.length === 0 ? (
                <div className="flex-grow flex flex-col items-center justify-center text-gray-500 p-6 text-center border border-dashed border-gray-700 rounded-lg">
                    <p className="font-medium text-gray-400">No complete sector efforts yet.</p>
                    <p className="text-xs mt-1">Riders appear here once their file passes the sector start and end.</p>
                </div>
            ) : (
                <div className="overflow-y-auto pr-2 -mr-2">
                    <table className="w-full text-left table-fixed border-collapse">
                        <thead className="sticky top-0 bg-gray-800 z-10 shadow-sm border-b border-gray-700">
                            <tr className="text-xs text-gray-400 uppercase">
                                <th className="p-2 w-10 text-center">Pl</th>
                                <th className="p-2">Rider</th>
                                <th className="p-2 w-20 text-right">Time</th>
                                <th className="p-2 w-16 text-right">Gap</th>
                                <th className="p-2 w-16 text-right">MPH</th>
                                {showPower && <th className="p-2 w-14 text-right">W</th>}
                            </tr>
                        </thead>
                        <tbody className="text-sm">
                            {rows.map(row => {
                                const isSpotlighted = row.athleteId === spotlightedAthleteId;
                                const isMe = !!currentUserId && row.userId === currentUserId;
                                return (
                                    <tr
                                        key={row.athleteId}
                                        onClick={() => onRowClick?.(row.athleteId)}
                                        className={`border-b border-gray-700/50 cursor-pointer transition-colors ${isSpotlighted ? 'bg-yellow-500/10' : isMe ? 'bg-cyan-500/10' : 'hover:bg-gray-700/50'}`}
                                    >
                                        <td className="p-2 text-center font-bold text-cyan-400">{row.rank}</td>
                                        <td className="p-2">
                                            <div className="flex items-center min-w-0">
                                                <span className="inline-block w-2 h-2 rounded-full mr-2 flex-shrink-0" style={{ backgroundColor: row.color }}></span>
                                                <span className="text-gray-200 truncate">{row.name}</span>
                                                {isMe && <span className="ml-1.5 flex-shrink-0 text-[10px] font-bold text-cyan-300 bg-cyan-900/60 px-1 py-0.5 rounded">YOU</span>}
                                            </div>
                                        </td>
                                        <td className="p-2 text-right font-mono text-white">{formatSectorTime(row.effort.elapsedMs)}</td>
                                        <td className="p-2 text-right font-mono text-gray-400">{row.gapMs > 0 ? `+${formatSectorTime(row.gapMs)}` : '—'}</td>
                                        <td className="p-2 text-right text-gray-300">{(row.effort.avgSpeedMps * 2.23694).toFixed(1)}</td>
                                        {showPower && <td className="p-2 text-right text-gray-300">{row.effort.avgPower !== null ? Math.round(row.effort.avgPower) : '—'}</td>}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {incomplete.length > 0 && (
                <p className="text-[11px] text-gray-500 mt-3">
                    No complete sector pass: {incomplete.map(i => i.name).join(', ')}
                </p>
            )}
        </div>
    );
};

export default RaceSectorResults;
